import React, { useState } from "react";

import {
  Alert,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { BinDeleteIcon } from "assets/material-custom-icons";
import { FileViewer } from "components/popups";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useMeasureParametersSelector } from "store";
import { IFile, MonitoringDataFiles, UiMeasureDataFiles } from "store/types";
import { formatDate } from "utils";
import { isPreviewableFileName } from "utils/files";

import { FileType } from "../../../constants";
import { IHandleDeleteFileFromMeasureData } from "./GroupsForm";
import { Group, MeasureData } from "./GroupsFormContext";

interface Props {
  measureData: MeasureData[];
  groups: Group[];
  handleDeleteFileFromMeasureData: ({ key, group_id, localId }: IHandleDeleteFileFromMeasureData) => void;
  filesErrors?: string[] | null;
}

export const FilesTable: React.FC<Props> = ({
  measureData,
  groups,
  handleDeleteFileFromMeasureData,
  filesErrors = null
}) => {
  const measureParameters = useMeasureParametersSelector();
  const [pdf, setPdf] = useState<(Pick<IFile, "file_url" | "type" | "name"> & { isBlobUrl?: boolean }) | null>(null);

  const columns = ["Name", "Parameter", "Investor", "Date", ""];
  const getParameter = (group_id: number) => {
    const group = groups.find((group) => group.id === group_id);
    return measureParameters.find((parameter) => parameter.id === group?.measure_id)?.name || "";
  };

  const getDateFromMeasureData = (group_id: number) => {
    const measure = measureData.find((measure) => measure.group_id === group_id);
    return measure?.date || "";
  };

  const mapFiles = (files: UiMeasureDataFiles | null) => ({
    localFiles: files?.localFiles
      ? Object.entries(files.localFiles).map(([localId, file]) => ({
          localId,
          fileName: file.name,
          file: file
        }))
      : [],
    dbFiles: files?.dbFiles
      ? Object.entries(files.dbFiles).map(([localId, file]) => ({
          localId,
          fileName: file.name,
          file: file
        }))
      : []
  });

  const groupFilesData = groups
    .filter((group) => group.files)
    .flatMap((group) => {
      const parameter = getParameter(group.id);
      const date = getDateFromMeasureData(group.id);
      return Object.entries(group.files || {}).map(([roundId, files]) => ({
        key: null,
        group_id: group.id,
        parameter,
        investor: "Total",
        date,
        roundId,
        ...mapFiles(files)
      }));
    });

  const measureFilesData = measureData
    .filter((item) => item.files)
    .map((item) => ({
      key: item.key,
      group_id: item.group_id,
      parameter: getParameter(item.group_id),
      investor: item.investor_name || "",
      date: item.date || "",
      roundId: item.round_id,
      ...mapFiles(item.files)
    }));

  const onFileNameClick = (file: File | MonitoringDataFiles, fileName: string) => {
    const isLocalFile = file instanceof File;

    if (isLocalFile) {
      const blobUrl = URL.createObjectURL(file);
      const fileType: FileType = FileType.PDF;

      setPdf({
        file_url: blobUrl,
        type: fileType,
        name: fileName,
        isBlobUrl: true
      });
    } else {
      const dbFile = file as MonitoringDataFiles;
      setPdf({
        file_url: dbFile.file_url,
        type: FileType.PDF,
        name: fileName,
        isBlobUrl: false
      });
    }
  };

  const hasFiles = Boolean(groupFilesData.length || measureFilesData.length);
  const hasFilesErrors = Boolean(filesErrors?.length);

  return (
    (hasFiles || hasFilesErrors) && (
      <Stack padding="20px" gap="5px">
        <Typography fontSize="16px" fontWeight="500">
          Files
        </Typography>

        {hasFilesErrors && (
          <Alert severity="error" sx={{ whiteSpace: "pre-line" }}>
            {filesErrors?.join("\n")}
          </Alert>
        )}

        {hasFiles && (
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: "8px",
              boxShadow: "none",
              border: "0.5px solid #dae0e2"
            }}
          >
            <Table stickyHeader>
              <TableHead sx={{ backgroundColor: "rgba(246, 248, 252, 1)" }}>
                <TableRow>
                  {columns.map((column, index) => (
                    <TableCell sx={cellCommonStyles} align={!index ? "left" : "center"} key={column}>
                      {column}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[...groupFilesData, ...measureFilesData].map((item) => (
                  <React.Fragment key={item.key}>
                    {/* New files */}
                    {item.localFiles.map((file) => (
                      <TableRowItem
                        item={item}
                        file={file}
                        handleDeleteFileFromMeasureData={handleDeleteFileFromMeasureData}
                        key={file.localId}
                        onFileNameClick={isPreviewableFileName(file.fileName) ? onFileNameClick : undefined}
                      />
                    ))}

                    {/* Existing files */}
                    {item.dbFiles.map((file) => (
                      <TableRowItem
                        item={item}
                        file={file}
                        handleDeleteFileFromMeasureData={handleDeleteFileFromMeasureData}
                        key={file.localId}
                        onFileNameClick={isPreviewableFileName(file.fileName) ? onFileNameClick : undefined}
                      />
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {pdf?.file_url && (
          <FileViewer
            file={pdf}
            isOpen={!!pdf}
            onClose={() => {
              if (pdf?.isBlobUrl && pdf?.file_url) {
                URL.revokeObjectURL(pdf.file_url);
              }
              setPdf(null);
            }}
          />
        )}
      </Stack>
    )
  );
};

const cellCommonStyles: TableCellProps["sx"] = {
  backgroundColor: "#edf1f9",
  color: "#0a3142",
  fontSize: "15px",
  fontWeight: "normal",
  border: "none",
  whiteSpace: "nowrap",
  padding: "9px 10px !important"
};

interface TableRowItemProps {
  item: {
    key: number | null;
    group_id: number;
    parameter: string;
    investor: string;
    date: string;
    roundId: number | string | null;
  };
  file: {
    localId: string;
    fileName: string;
    file: File | MonitoringDataFiles;
  };
  handleDeleteFileFromMeasureData: ({ key, group_id, localId, roundId }: IHandleDeleteFileFromMeasureData) => void;
  onFileNameClick?: (file: File | MonitoringDataFiles, fileName: string) => void;
}

const TableRowItem: React.FC<TableRowItemProps> = ({
  item,
  file,
  handleDeleteFileFromMeasureData,
  onFileNameClick
}) => {
  return (
    <TableRow key={file.localId}>
      <TableCell sx={{ maxWidth: "100px" }}>
        <MaterialTooltip title={file.fileName}>
          <Typography
            onClick={onFileNameClick ? () => onFileNameClick(file.file, file.fileName) : undefined}
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              cursor: onFileNameClick ? "pointer" : "default",
              color: onFileNameClick ? "#26a6ae" : "#0a3142"
            }}
          >
            {file.fileName}
          </Typography>
        </MaterialTooltip>
      </TableCell>
      <TableCell sx={{ maxWidth: "100px" }}>
        <MaterialTooltip title={item.parameter}>
          <Typography sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {item.parameter}
          </Typography>
        </MaterialTooltip>
      </TableCell>
      <TableCell sx={{ maxWidth: "100px" }}>
        <MaterialTooltip title={item.investor}>
          <Typography sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {item.investor || "Total"}
          </Typography>
        </MaterialTooltip>
      </TableCell>
      <TableCell sx={{ maxWidth: "100px" }}>
        <MaterialTooltip title={formatDate({ date: item.date, format: "DD/MM/YYYY" })}>
          <Typography sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {formatDate({ date: item.date, format: "DD/MM/YYYY" })}
          </Typography>
        </MaterialTooltip>
      </TableCell>
      <TableCell>
        <IconButton
          onClick={() =>
            handleDeleteFileFromMeasureData({
              key: item.key || null,
              group_id: item.group_id,
              localId: file.localId,
              roundId: item.roundId
            })
          }
        >
          <BinDeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};
